import React, { useCallback, useMemo } from 'react';

import { Box } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { SceneRendererProps } from 'react-native-tab-view';

import { Helmet } from 'components/helmet';
import { LayoutContainer } from 'components/layout/styled';
import LoadingAnimation from 'components/loading-animation';
import useRedirectAuthIfBrazeId from 'components/phone-from-braze-id/use-redirect-auth-if-braze-id';
import { TabView } from 'components/tab-view';
import { SingleAuthTabView, useEnableSingleAuth } from 'experiments/single-auth';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRoute } from 'hooks/navigation/use-route';
import useEffectOnce from 'hooks/use-effect-once';
import { useFeatureAccountForm } from 'hooks/use-feature-account-form';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import CampaignHeadline from 'pages/authentication/components/auth-campaigns/campaign-headline';
import useAuthCampaign from 'pages/authentication/components/auth-campaigns/hooks/use-auth-campaign';
import { useEnableSignUpSignInABTest } from 'pages/authentication/use-enable-sign-up-sign-in-a-b-test';
import useAuthFlow from 'state/auth/hooks/use-auth-flow';
import { IAuthRouteParams } from 'state/auth/types';
import { useScrollContext } from 'state/scroll';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';

import { AUTH_CAMPAIGN_PARAM_ID } from '../components/auth-campaigns/constants';
import SignUpHeadline from '../components/sign-up-headline/sign-up-headline';

import SignInTab from './sign-in-tab';
import { AuthContainer, SignUpContainer, SignUpHomeContainer } from './sign-up-home.styled';
import SignUpTab from './sign-up-tab';

type AuthenticationTabKey = 'signUp' | 'signIn';

const SignupHome = () => {
  useRedirectAuthIfBrazeId();
  const isMobile = useIsMobileBreakpoint();
  const enableSignUpSignInABTest = useEnableSignUpSignInABTest();
  const { formatMessage } = useIntl();
  const { scrollRef } = useScrollContext();
  const { navigate } = useNavigation();
  const { params, pathname } = useRoute<IAuthRouteParams & { [AUTH_CAMPAIGN_PARAM_ID]?: string }>();
  const { featureAccountForm, featureAccountFormLoading } = useFeatureAccountForm();
  const campaignIdParam = params[AUTH_CAMPAIGN_PARAM_ID];
  const { attemptedSignUpWithExistingEmail } = params;

  const campaignContent = featureAccountForm?.activeCampaigns?.find(
    campaign => campaign?.campaignId === campaignIdParam
  );
  const { extraFields } = useAuthCampaign({
    campaignId: campaignIdParam,
    campaignContent,
  });

  const { activeRouteIsSignIn, showOtpForm } = useAuthFlow();
  const activeTabIndex = activeRouteIsSignIn ? 1 : 0;
  const enableSingleAuthTab = useEnableSingleAuth();
  // TODO React Navigation: we may need to update this to a useEffect
  // if RN Nav isn't update on each load
  useEffectOnce(() => {
    emitEvent(EventName.SCREEN_RENDER, {
      screenId: 'signupHome',
    });
  });

  const tabs = useMemo(
    () => [
      { key: 'signUp', title: formatMessage({ id: 'signUp' }) },
      { key: 'signIn', title: formatMessage({ id: 'signIn' }) },
    ],
    [formatMessage]
  );

  // As we need to pass additional props, we have to use a custom renderScene function instead of SceneMap
  // Do not return inline functions because If you pass inline functions, it'll re-create the component every render, which will cause the entire route to unmount and remount every change. It's very bad for performance and will also cause any local state to be lost.
  const renderScene = useCallback(
    ({ route: { key } }: SceneRendererProps & { route: { key: AuthenticationTabKey } }) => {
      if (!key) {
        return null;
      }

      const component =
        key === 'signUp' ? (
          <SignUpTab showOtpForm={showOtpForm} extraFields={extraFields} />
        ) : (
          <SignInTab
            showOtpForm={showOtpForm}
            extraFields={extraFields}
            attemptedSignUpWithExistingEmail={attemptedSignUpWithExistingEmail}
          />
        );

      return (
        <KeyboardAwareScrollView keyboardShouldPersistTaps="handled">
          {component}
        </KeyboardAwareScrollView>
      );
    },
    [extraFields, showOtpForm, attemptedSignUpWithExistingEmail]
  );

  const handleTabIndexChange = useCallback(
    (newIndex: number) => {
      navigate(routes.signUp, {
        state: { ...params, activeRouteIsSignIn: newIndex === 1 },
        replace: true,
      });
    },
    [navigate, params]
  );

  let TabViewComponent = (
    <TabView
      keyboardDismissMode={'on-drag'}
      activeTabIndex={activeTabIndex}
      handleTabIndexChange={handleTabIndexChange}
      tabs={tabs}
      // @ts-expect-error TS(2322) FIXME: Type '({ route: { key } }: SceneRendererProps & { ... Remove this comment to see the full error message
      renderScene={renderScene}
    />
  );
  if (enableSingleAuthTab) {
    TabViewComponent = <SingleAuthTabView />;
  }

  let component = null;
  if (enableSignUpSignInABTest) {
    component =
      pathname.includes(routes.signUp) && !showOtpForm ? (
        <SignUpTab showOtpForm={showOtpForm} extraFields={extraFields} />
      ) : (
        <SignInTab
          showOtpForm={showOtpForm}
          extraFields={extraFields}
          attemptedSignUpWithExistingEmail={attemptedSignUpWithExistingEmail}
        />
      );
  }

  return (
    <LayoutContainer isFullContainer={isMobile}>
      <Helmet title={formatMessage({ id: 'signUp' })} />
      <KeyboardAwareScrollView
        ref={ref => (scrollRef.current = ref)}
        contentContainerStyle={{ flexGrow: 1, flexShrink: 1 }}
        // Enable only extraScrollHeight on signIn in order to show the input and submit button
        extraScrollHeight={activeRouteIsSignIn ? 100 : 80}
        keyboardShouldPersistTaps="handled"
        enableAutomaticScroll={false}
      >
        <SignUpHomeContainer
          testID="signup-home-container"
          $backgroundColor={campaignContent?.campaingBackgroundColor?.hex}
        >
          <AuthContainer>
            <Box alignItems={'center'} minHeight={'150px'} paddingY="$4">
              {campaignIdParam && featureAccountFormLoading ? (
                <LoadingAnimation />
              ) : campaignContent ? (
                <CampaignHeadline campaign={campaignContent} />
              ) : (
                <SignUpHeadline />
              )}
            </Box>
            <SignUpContainer isSignIn={activeRouteIsSignIn}>
              {enableSignUpSignInABTest ? component : TabViewComponent}
            </SignUpContainer>
          </AuthContainer>
        </SignUpHomeContainer>
      </KeyboardAwareScrollView>
    </LayoutContainer>
  );
};

export default React.memo(SignupHome);
